import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useMemo, useState } from 'react';
import { useConnection } from 'wagmi';

import CheckmarkIcon from '@/app/assets/svg/checkmark.svg?react';
import CopyIcon from '@/app/assets/svg/copy.svg?react';
import ErrorIcon from '@/app/assets/svg/error.svg?react';
import InfoIcon from '@/app/assets/svg/info.svg?react';
import XIcon from '@/app/assets/svg/x.svg?react';
import { useReferralActions } from '@/app/components/ReferralPageComponents/hooks/useReferralActions';
import { useReferralSummary } from '@/app/components/ReferralPageComponents/hooks/useReferralSummary';
import ReferralActionRow from '@/app/components/ReferralPageComponents/ReferralActionRow';
import { REFERRAL_ACTION_BUTTON_CLASS } from '@/app/components/ReferralPageComponents/referralConstants';
import {
  buildReferralLink,
  buildShareOnXUrl,
  isValidReferralCodeFormat,
  normalizeReferralCode,
  REFERRAL_ERRORS,
} from '@/app/components/ReferralPageComponents/referralValidation';
import { useCopyToClipboard } from '@/app/shared/hooks/useCopyToClipboard';
import { cn } from '@/app/shared/lib/cn';
import { toReferralApiCode } from '@/app/shared/lib/referralSigning';
import { getReferralCodeCheck } from '@/app/shared/services/api';
import { Button } from '@/app/shared/ui/Button';
import { Input } from '@/app/shared/ui/Input';
import { Skeleton } from '@/app/shared/ui/Skeleton';

export type CreateReferralState = 'idle' | 'creating' | 'created';

const ReferralCreateCode = () => {
  const { isConnected } = useConnection();
  const { openConnectModal } = useConnectModal();
  const { isCopied, copy } = useCopyToClipboard();
  const { code: existingCode, isLoading: isSummaryLoading } = useReferralSummary();
  const { createCode, mapReferralError } = useReferralActions();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<CreateReferralState>('idle');
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  const resolvedCreatedCode =
    createdCode ?? (existingCode ? normalizeReferralCode(existingCode) : null);
  const isCreatedView = Boolean(resolvedCreatedCode);
  const showSummarySkeleton = isConnected && isSummaryLoading;

  const normalizedCode = normalizeReferralCode(code);
  const hasValue = normalizedCode.length > 0;

  const formatError = useMemo(() => {
    if (!hasValue) return null;
    if (!isValidReferralCodeFormat(normalizedCode)) return REFERRAL_ERRORS.INVALID_FORMAT;
    return null;
  }, [hasValue, normalizedCode]);

  const displayError = error ?? formatError;
  const isCreating = state === 'creating';
  const isCreateDisabled = isCreating || isCreatedView || !hasValue || !!formatError || !!error;

  const referralLink = resolvedCreatedCode ? buildReferralLink(resolvedCreatedCode) : '';

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setCode(normalizeReferralCode(event.target.value));
  };

  const handleCreate = async () => {
    if (isCreatedView || state !== 'idle' || !normalizedCode || !!formatError) return;

    if (!isValidReferralCodeFormat(normalizedCode)) {
      setError(REFERRAL_ERRORS.INVALID_FORMAT);
      return;
    }

    if (!isConnected) {
      openConnectModal?.();
      return;
    }

    setError(null);
    setState('creating');

    try {
      const check = await getReferralCodeCheck(toReferralApiCode(normalizedCode));
      if (!check?.valid) {
        setError(REFERRAL_ERRORS.CODE_UNAVAILABLE);
        setState('idle');
        return;
      }
      if (check.exists) {
        setError(REFERRAL_ERRORS.CODE_TAKEN);
        setState('idle');
        return;
      }

      const result = await createCode(normalizedCode);
      setCreatedCode(normalizeReferralCode(result.code));
      setState('created');
    } catch (err) {
      setError(mapReferralError(err));
      setState('idle');
    }
  };

  const handleCopy = () => {
    void copy(referralLink);
  };

  return (
    <ReferralActionRow
      title='Create Your Referral Code'
      description='Create or copy your custom alias code to invite friends and receive passive point cuts.'
    >
      {showSummarySkeleton ? (
        <div className='flex flex-col gap-3.5'>
          <Skeleton.Line loading className='h-12 w-full' />
          <Skeleton.Line loading className='h-5 w-2/3' />
        </div>
      ) : isCreatedView && resolvedCreatedCode ? (
        <div className='flex flex-col gap-3.5'>
          <div className='flex flex-col gap-5 lg:flex-row lg:items-start'>
            <Input
              value={referralLink}
              readOnly
              limitLength={false}
              className='min-w-0 w-full flex-1 rounded-md border border-gray-700 bg-transparent px-4 py-2.75 text-base leading-6 font-normal text-modal-result-body focus:border-modal-result-body [:root.light_&]:border-gray-900 [:root.light_&]:focus:border-gray-700'
            />
            <div className='flex w-full shrink-0 flex-nowrap gap-5 sm:w-auto'>
              <Button
                variant='ghost'
                onClick={handleCopy}
                className={cn(REFERRAL_ACTION_BUTTON_CLASS, 'min-w-0 flex-1 gap-3 sm:w-34 sm:flex-none')}
              >
                <span className='inline-flex min-w-14 justify-center'>{isCopied ? 'Copied' : 'Copy'}</span>
                {isCopied ? <CheckmarkIcon className='h-3.5 w-3.5 shrink-0' /> : <CopyIcon className='h-4 w-4 shrink-0' />}
              </Button>
              <a
                href={buildShareOnXUrl(referralLink)}
                target='_blank'
                rel='noopener noreferrer'
                className={cn(
                  REFERRAL_ACTION_BUTTON_CLASS,
                  'inline-flex min-w-0 flex-1 items-center justify-center gap-2 sm:w-34 sm:flex-none',
                )}
              >
                Share on
                <XIcon className='h-4 w-4' />
              </a>
            </div>
          </div>
          <div className='flex min-h-5 items-center gap-1.5 text-sm leading-5 text-gray-400'>
            <InfoIcon className='h-3.5 w-3.5 shrink-0' />
            <span>Referral codes cannot be changed after creation.</span>
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-5 lg:flex-row lg:items-start'>
          <div className='flex min-w-0 flex-1 flex-col gap-2'>
            <Input
              value={code}
              onChange={handleCodeChange}
              placeholder='Enter code (A–Z, 0–9, -, _ )'
              disabled={isCreating}
              limitLength={false}
              className={cn(
                'w-full rounded-md border border-gray-700 bg-transparent px-4 py-2.75 text-base leading-6 font-normal text-gray-200 placeholder:text-gray-400 focus:border-modal-result-body [:root.light_&]:border-gray-900 [:root.light_&]:focus:border-gray-700',
                !!normalizedCode && !displayError && 'text-brand-1100',
                displayError &&
                  'border-feedback-error text-brand-1100 focus:border-feedback-error [:root.light_&]:border-feedback-error [:root.light_&]:focus:border-feedback-error',
              )}
            />
            {displayError ? (
              <div className='flex items-center gap-1.5 text-sm text-gray-400'>
                <ErrorIcon className='h-4 w-4' />
                <span>{displayError}</span>
              </div>
            ) : (
              <div className='hidden lg:block lg:min-h-6.5' />
            )}
          </div>

          <Button
            variant='ghost'
            onClick={handleCreate}
            disabled={isCreateDisabled}
            className={cn(
              REFERRAL_ACTION_BUTTON_CLASS,
              'w-full lg:w-47.5',
              isCreating && 'disabled:bg-action-button disabled:opacity-100',
            )}
          >
            {isCreating ? (
              <span className='flex items-center gap-2'>
                <span className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                Creating link...
              </span>
            ) : (
              'Create referral code'
            )}
          </Button>
        </div>
      )}
    </ReferralActionRow>
  );
};

export default ReferralCreateCode;
